import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, CheckCircle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingSection = () => {
  const [formData, setFormData] = useState({
    owner_name: "",
    phone: "",
    email: "",
    pet_type: "",
    breed: "",
    preferred_date: null,
    service_type: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, preferred_date: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.owner_name || !formData.phone || !formData.email || !formData.pet_type || !formData.preferred_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        preferred_date: format(formData.preferred_date, "yyyy-MM-dd"),
      };

      await axios.post(`${API}/appointments`, payload);

      setIsSubmitted(true);
      toast.success("Appointment request submitted successfully!");

      // Reset form after delay
      setTimeout(() => {
        setFormData({
          owner_name: "",
          phone: "",
          email: "",
          pet_type: "",
          breed: "",
          preferred_date: null,
          service_type: "",
        });
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error("Error submitting appointment:", error);
      toast.error("Failed to submit appointment. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        id="booking"
        data-testid="booking-section"
        className="py-16 md:py-24 bg-gradient-to-br from-sky-50 to-white"
      >
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div
            data-testid="booking-success"
            className="text-center py-16 animate-fade-in"
          >
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
              Thank You!
            </h3>
            <p className="text-lg text-slate-600">
              Your appointment request has been received. We'll contact you shortly to confirm your booking.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-16 md:py-24 bg-gradient-to-br from-sky-50 to-white"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Book Your Pet's Appointment
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-lg text-slate-600">
            Fill out the form below and we'll get back to you to confirm your appointment.
          </p>
        </div>

        {/* Booking Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 animate-fade-in-up animation-delay-200"
        >
          <div className="grid gap-6">
            {/* Owner Name */}
            <div className="space-y-2">
              <Label htmlFor="owner_name" className="text-slate-700 font-medium">
                Pet Owner Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="owner_name"
                name="owner_name"
                data-testid="input-owner-name"
                value={formData.owner_name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                required
              />
            </div>

            {/* Phone & Email */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="input-phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="input-email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  required
                />
              </div>
            </div>

            {/* Pet Type & Breed */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">
                  Pet Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.pet_type}
                  onValueChange={(value) => handleSelectChange("pet_type", value)}
                >
                  <SelectTrigger
                    data-testid="select-pet-type"
                    className="h-12 rounded-xl border-slate-200"
                  >
                    <SelectValue placeholder="Select pet type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Dog</SelectItem>
                    <SelectItem value="cat">Cat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="breed" className="text-slate-700 font-medium">
                  Breed (Optional)
                </Label>
                <Input
                  id="breed"
                  name="breed"
                  data-testid="input-breed"
                  value={formData.breed}
                  onChange={handleInputChange}
                  placeholder="e.g., Golden Retriever"
                  className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
            </div>

            {/* Preferred Date */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Preferred Date <span className="text-red-500">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="select-date"
                    className={`w-full h-12 rounded-xl border-slate-200 justify-start text-left font-normal ${
                      !formData.preferred_date && "text-slate-400"
                    }`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.preferred_date ? (
                      format(formData.preferred_date, "PPP")
                    ) : (
                      <span>Select a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.preferred_date}
                    onSelect={handleDateChange}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Service Type */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Service Type (Optional)
              </Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => handleSelectChange("service_type", value)}
              >
                <SelectTrigger
                  data-testid="select-service-type"
                  className="h-12 rounded-xl border-slate-200"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bath">Bath & Blow Dry</SelectItem>
                  <SelectItem value="haircut">Hair Trimming</SelectItem>
                  <SelectItem value="nails">Nail Clipping</SelectItem>
                  <SelectItem value="full">Full Grooming Package</SelectItem>
                  <SelectItem value="deshed">De-shedding Treatment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="submit-booking"
              disabled={isSubmitting}
              className="w-full h-14 rounded-full text-base font-semibold bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg shadow-sky-200 transition-all hover:-translate-y-0.5 mt-4"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Reserve Appointment"
              )}
            </Button>
          </div>

          {/* Privacy Note */}
          <p
            data-testid="privacy-note"
            className="text-center text-sm text-slate-500 mt-6"
          >
            We respect your privacy. Your information will only be used to schedule your appointment.
          </p>
        </form>
      </div>
    </section>
  );
};

export default BookingSection;
